import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import { FormGroup, Classes, Intent } from '@blueprintjs/core';
import intl from 'react-intl-universal';

import { CellType } from '@/constants';
import { ItemsSuggestField } from '@/components/Items/ItemsSuggestField';

import { useCellAutoFocus } from '@/hooks';
import { useCreateAutofillListener } from '@/hooks/state/autofill';

/**
 * Items list cell.
 */
export default function ItemsListCell({
  column: { id, filterSellable, filterPurchasable, fieldProps, formGroupProps },
  row: { index },
  cell: { value: initialValue },
  payload: { items, updateData, errors, autoFocus },
}: any) {
  const fieldRef = useRef();

  // Auto-focus the items list input field.
  useCellAutoFocus(fieldRef, autoFocus, id, index);

  // Handle the item selected.
  const handleItemSelected = useCallback(
    (item: any) => {
      updateData(index, id, item.id);
    },
    [updateData, index, id],
  );

  // Create autofill listener to auto-select newly created items
  const autofillRef = useCreateAutofillListener((payload: any) => {
    if (payload?.itemId) {
      updateData(index, id, payload.itemId);
    }
  });

  const error = errors?.[index]?.[id];

  return (
    <FormGroup
      intent={error ? Intent.DANGER : undefined}
      className={classNames('form-group--select-list', Classes.FILL)}
      {...formGroupProps}
    >
      <ItemsSuggestField
        items={items}
        onItemSelected={handleItemSelected}
        selectedItemId={initialValue}
        sellable={filterSellable}
        purchasable={filterPurchasable}
        autofillRef={autofillRef}
        inputProps={{
          inputRef: (ref: any) => (fieldRef.current = ref),
          placeholder: intl.get('enter_an_item'),
        }}
        openOnKeyDown={true}
        blurOnSelectClose={false}
        {...fieldProps}
      />
    </FormGroup>
  );
}

ItemsListCell.cellType = CellType.Field;
